import { assetUrl } from "./asset-url.js";

const cards = (items) => items.map(([title, text]) => ({ title, text }));

const makeDetail = ({
  headline,
  introTitle,
  intro,
  featureTitle,
  features,
  flowTitle,
  flow,
  contextsTitle,
  contexts,
  status = "Public platform concept",
  access = "Public overview; deeper evidence by qualified request",
  limitation = "Performance and availability depend on program context, evidence, and region.",
  related = [],
  ctaTitle = "Explore a program with Medein.",
  ctaText = "Share the scientific or product context your team wants to evaluate.",
}) => ({
  headline,
  introTitle,
  intro,
  featureTitle,
  features: cards(features),
  flowTitle,
  flow,
  contextsTitle,
  contexts: cards(contexts),
  evidence: [
    ["Content status", status, "Reviewed for public communication"],
    ["Evidence access", access, "Subject to scientific and access review"],
    ["Review focus", "Scope, assumptions, limitations, and next evidence gate", "Maintained by the program owner"],
    ["Important limitation", limitation, "No universal or clinical performance is implied"],
  ],
  related,
  ctaTitle,
  ctaText,
});

const productDetail = ({
  headline,
  introTitle,
  intro,
  features,
  flow,
  contexts,
  status,
  limitation,
  related = [],
}) =>
  makeDetail({
    headline,
    introTitle,
    intro,
    featureTitle: "Product system",
    features,
    flowTitle: "From inquiry to reviewed use",
    flow,
    contextsTitle: "Intended public context",
    contexts,
    status,
    limitation,
    related,
    ctaTitle: "Request product information.",
    ctaText: "Tell us the application, region, evidence requirement, and intended use so the appropriate team can respond.",
  });

export const overviewContent = {
  science: {
    introTitle: "One science layer, four connected technologies.",
    intro:
      "Medein’s science system moves from structured biological design to construction, assembly, delivery research, and direct measurement. Each technology keeps assumptions and quality evidence visible.",
    flowTitle: "How the science connects",
    flow: ["Design", "Build", "Assemble", "Deliver", "Count"],
    values: cards([
      ["Structure", "Complex programs are decomposed into reviewable modules and interfaces."],
      ["Traceability", "Versions, evidence inputs, and quality decisions remain connected."],
      ["Measurement", "Assays are designed to return decision-grade evidence to the platform."],
      ["Responsible access", "Public concepts remain separate from controlled technical material."],
    ]),
  },
  platform: {
    introTitle: "A governed design-to-validation loop.",
    intro:
      "The platform links biological context, candidate design, simulation, assay execution, and evidence review. Modules can stand alone or operate as an integrated program.",
    flowTitle: "Design-to-validation loop",
    flow: ["Problem", "Model", "Design", "Simulate", "Assay", "Learn"],
    values: cards([
      ["Evidence-aware design", "Candidate logic stays connected to its supporting biological context."],
      ["Visible uncertainty", "Confidence and model boundaries are part of the decision record."],
      ["Modular integration", "Programs select the modules and interfaces they need."],
      ["Learning loop", "Validation results inform the next design and review cycle."],
    ]),
  },
  applications: {
    introTitle: "Shared platform logic, focused application programs.",
    intro:
      "Four application domains organize product concepts and pipeline programs without collapsing research, commercial, and regulated status into a single claim.",
    flowTitle: "Shared application logic",
    flow: ["Data", "Model", "Design", "Validate", "Deploy"],
    values: cards([
      ["Precision Oncology", "Biomarker and molecular-measurement research."],
      ["Industrial Resilience", "Biological materials, process observation, and operational continuity."],
      ["Biodefense and CBRN Threat", "Governed preparedness, sensing, and controlled evidence."],
      ["Longevity", "Cellular state, resilience, and healthy-aging biology research."],
    ]),
  },
  "product-line": {
    introTitle: "A portfolio organized by application and status.",
    intro:
      "Medein presents product lines, product concepts, and research concepts with explicit evidence and availability boundaries. Product pages do not substitute for approved specifications or regional labeling.",
    flowTitle: "Portfolio pathway",
    flow: ["Platform capability", "Product concept", "Evidence review", "Regional decision", "Supported use"],
    values: cards([
      ["Clear category", "Every item is mapped to an application area."],
      ["Explicit status", "Product line, product concept, and research concept are separated."],
      ["Evidence-linked", "Public statements connect to a defined review level."],
      ["Region-aware", "Availability is confirmed only through qualified inquiry."],
    ]),
  },
  pipeline: {
    introTitle: "Programs progress through evidence gates.",
    intro:
      "Pipeline pages communicate rationale, current stage, next milestone, evidence gate, and partnering context—without implying probability of success or approved use.",
    flowTitle: "Development gates",
    flow: ["Discovery", "Feasibility", "Validation", "Productization", "Deployment review"],
    values: cards([
      ["Program rationale", "The problem and platform fit are stated clearly."],
      ["Current stage", "Research status is separated from product availability."],
      ["Next milestone", "The next public evidence gate is identified."],
      ["Partnering", "Collaboration scope and access requirements are visible."],
    ]),
  },
};

export const detailContent = {
  science: {
    "3d-million-base-dna-synthesis": makeDetail({
      headline: "Build DNA at million-base scale—with structure, traceability, and control.",
      introTitle: "Large architectures need a different quality model.",
      intro:
        "As designed DNA systems become larger, identity, integrity, completeness, and version control become system-level concerns. Medein frames large-scale construction as a modular architecture with quality nodes at every interface.",
      featureTitle: "Platform concept",
      features: [
        ["3D structured design", "Architecture is organized across sequence, module, interface, and assembly levels."],
        ["Modular construction", "Large designs are decomposed into buildable and independently reviewable units."],
        ["Quality nodes", "Identity and integrity checks are attached to defined transition points."],
        ["Version traceability", "Design intent, revisions, and evidence records remain linked."],
      ],
      flowTitle: "Quality pathway",
      flow: ["Architect", "Partition", "Construct", "Verify", "Integrate"],
      contextsTitle: "Research spaces",
      contexts: [
        ["Genome-scale engineering", "Structured programs requiring clear module and interface ownership."],
        ["Cell-factory research", "Large biological designs evaluated in controlled development programs."],
        ["Reference constructs", "Traceable architectures used for measurement and comparison research."],
      ],
      limitation: "This page describes a research platform concept, not a validated universal synthesis performance claim.",
    }),
    "umbert-genome-assembly": makeDetail({
      headline: "Genome assembly that learns context, structure, and uncertainty.",
      introTitle: "Assembly is an evidence interpretation problem.",
      intro:
        "UMBERT organizes heterogeneous genomic evidence into an assembly graph that makes structural hypotheses, confidence, and review priorities explicit.",
      featureTitle: "UMBERT framework",
      features: [
        ["Evidence ingestion", "Sequence, structural, and contextual inputs enter with source and quality metadata."],
        ["Context model", "Biological and technical context informs candidate interpretation."],
        ["Assembly graph", "Alternative structures remain visible instead of being silently collapsed."],
        ["Confidence layer", "Uncertainty and human-review queues are part of the output."],
      ],
      flowTitle: "Interpretation workflow",
      flow: ["Ingest", "Contextualize", "Assemble", "Score", "Review"],
      contextsTitle: "Decision outputs",
      contexts: [
        ["Assembly hypotheses", "Candidate structures with supporting and conflicting evidence."],
        ["Confidence map", "Quality-aware signals for downstream review and benchmarking."],
        ["Review queue", "Prioritized regions where additional evidence may change interpretation."],
      ],
      limitation: "Benchmark performance is dataset- and protocol-dependent and is not represented as a universal result.",
    }),
    "artificial-delivery-vehicle-protein": makeDetail({
      headline: "Engineer protein vehicles for targeted biological delivery research.",
      introTitle: "Delivery design must balance interacting constraints.",
      intro:
        "This research platform explores modular protein vehicle concepts across targeting, payload compatibility, stability, specificity, and manufacturability.",
      featureTitle: "Design principles",
      features: [
        ["Modularity", "Vehicle, targeting, and payload-interaction functions are treated as configurable modules."],
        ["Specificity", "Candidate logic is evaluated against intended and unintended interaction contexts."],
        ["Payload compatibility", "Design assumptions reflect payload class and program constraints."],
        ["Developability", "Characterization includes stability and manufacturability considerations."],
      ],
      flowTitle: "Platform loop",
      flow: ["Model", "Design", "Express", "Characterize", "Refine"],
      contextsTitle: "Research contexts",
      contexts: [
        ["Oncology research", "Selective-delivery concepts explored within governed partner programs."],
        ["Cell engineering", "Modular research tools for controlled biological interaction studies."],
        ["Platform tools", "Vehicle concepts used to test design and characterization workflows."],
      ],
      limitation: "No clinical safety, efficacy, targeting, or delivery performance is claimed on this public page.",
    }),
    "direct-molecular-counting": makeDetail({
      headline: "Count molecules directly. Preserve the signal.",
      introTitle: "Low-abundance signals need auditable measurement.",
      intro:
        "Direct Molecular Counting is framed as a measurement workflow that converts individual events into calibrated, quality-aware outputs while preserving the path from signal to report.",
      featureTitle: "Measurement dimensions",
      features: [
        ["Capture", "The measurement design defines target, matrix, controls, and event capture logic."],
        ["Classification", "Individual events are classified with explicit thresholds and review rules."],
        ["Calibration", "Corrections and calibration connect raw events to interpretable quantities."],
        ["Reporting", "Quality-control results and limitations travel with the measurement."],
      ],
      flowTitle: "Measurement workflow",
      flow: ["Capture", "Classify", "Correct", "Calibrate", "Report"],
      contextsTitle: "Research use cases",
      contexts: [
        ["Rare-signal research", "Programs where low-abundance events require careful quality interpretation."],
        ["Single-molecule studies", "Research workflows centered on discrete molecular observations."],
        ["Assay quality control", "Reference and comparison studies for measurement-system development."],
      ],
      limitation: "LOD, precision, specificity, and throughput require assay-specific validation and are not generalized here.",
    }),
  },
  platform: {
    "programmable-life-design-core-ai-engine": makeDetail({
      headline: "Design biology with a core AI engine built for evidence.",
      introTitle: "Candidate generation is only useful when reasoning remains reviewable.",
      intro:
        "The Core AI Engine connects biological context, constraints, candidate generation, ranking, confidence, and validation feedback in one governed design record.",
      featureTitle: "Reasoning architecture",
      features: [
        ["Context graph", "Evidence and constraints are organized around the program question."],
        ["Candidate generation", "Design alternatives are produced within declared boundaries."],
        ["Ranking and uncertainty", "Prioritization includes confidence and unresolved assumptions."],
        ["Evidence trace", "Recommendations remain connected to their inputs and review history."],
      ],
      flowTitle: "Learning loop",
      flow: ["Frame", "Generate", "Rank", "Validate", "Learn"],
      contextsTitle: "Program outputs",
      contexts: [
        ["Design recommendations", "Reviewable candidates with rationale and constraints."],
        ["Experiment priorities", "Validation plans focused on the most decision-relevant questions."],
        ["Confidence record", "Visible uncertainty, evidence gaps, and escalation points."],
      ],
      limitation: "AI outputs require scientific review and do not replace experimental validation or professional judgment.",
    }),
    "synthetic-epigenome-architecture": makeDetail({
      headline: "Design regulatory state—not only sequence.",
      introTitle: "Biological state emerges from interacting regulatory layers.",
      intro:
        "The Synthetic Epigenome Architecture module models chromatin, methylation, histone state, transcriptional networks, and functional readouts as a connected design space.",
      featureTitle: "Regulatory layers",
      features: [
        ["Chromatin context", "Accessibility and structural context are represented as design constraints."],
        ["Methylation logic", "State hypotheses include locus- and context-dependent methylation evidence."],
        ["Histone state", "Regulatory marks are evaluated as part of a multi-layer state model."],
        ["Network behavior", "Transcriptional relationships connect molecular state to downstream readouts."],
      ],
      flowTitle: "Design framework",
      flow: ["Define state", "Model intervention", "Assess off-targets", "Plan validation", "Monitor"],
      contextsTitle: "Readout strategy",
      contexts: [
        ["Multi-omics", "State interpretation across complementary molecular evidence."],
        ["Expression", "Transcriptional readouts linked to the intended state hypothesis."],
        ["Functional phenotype", "Program-specific assays used to test whether state change is meaningful."],
      ],
      limitation: "This is a research architecture; reversibility, specificity, and safety require program-specific evidence.",
    }),
    "programmable-life-surrogate": makeDetail({
      headline: "Simulate biological decisions before committing experiments.",
      introTitle: "A surrogate supports choices; it does not replace biology.",
      intro:
        "Programmable Life Surrogate compares scenarios, sensitivity, expected value, and uncertainty so teams can prioritize experiments with the greatest information value.",
      featureTitle: "Model scope",
      features: [
        ["Multi-scale models", "Molecular, cellular, assay, process, and population scopes can be separated."],
        ["Calibration", "Parameters and assumptions are tied to available evidence."],
        ["Scenario comparison", "Alternative designs are compared under explicit conditions."],
        ["Human review", "Model limits and drift remain visible to decision owners."],
      ],
      flowTitle: "Surrogate workflow",
      flow: ["Define", "Calibrate", "Simulate", "Rank", "Validate"],
      contextsTitle: "Decision contexts",
      contexts: [
        ["Experiment prioritization", "Select experiments expected to resolve the most important uncertainty."],
        ["Design exploration", "Compare candidate trajectories before physical execution."],
        ["Process research", "Explore sensitivity and operational tradeoffs in controlled models."],
      ],
      limitation: "Surrogate results are conditional on model scope, calibration data, and external validation.",
    }),
    "precision-assays": makeDetail({
      headline: "Assays designed for decision-grade evidence.",
      introTitle: "An assay begins with the decision it must support.",
      intro:
        "Precision Assays connects target definition, matrix, controls, analytical validation, quality systems, and translational context.",
      featureTitle: "Assay architecture",
      features: [
        ["Decision definition", "Target, intended decision, matrix, and threshold are stated before development."],
        ["Analytical validation", "Accuracy, precision, linearity, specificity, robustness, and LOD are assessed as relevant."],
        ["Quality system", "Calibration, controls, traceability, and change control support reproducibility."],
        ["Translation path", "Research, partner validation, and regulated pathways remain distinct."],
      ],
      flowTitle: "Assay lifecycle",
      flow: ["Define", "Design", "Validate", "Transfer", "Monitor"],
      contextsTitle: "Assay formats",
      contexts: [
        ["Molecular counting", "Direct or calibrated counting for research measurement."],
        ["Sequencing", "Quality-aware genomic evidence generation and interpretation."],
        ["Immunoassay and cell-based", "Program-specific formats selected around the decision need."],
      ],
      limitation: "RUO, IUO, LDT, and IVD status must be confirmed separately for each assay and region.",
    }),
  },
  applications: {
    "precision-oncology": makeDetail({
      headline: "Connect molecular signals to interpretable oncology research.",
      introTitle: "Precision begins with evidence quality.",
      intro:
        "Medein combines biomarker research, direct molecular measurement, governed interpretation, and validation planning for precision-oncology programs.",
      featureTitle: "Platform approach",
      features: [
        ["Discovery", "Define the biological question, cohort context, and decision need."],
        ["Counting", "Select measurement strategies appropriate to signal abundance and matrix."],
        ["Interpretation", "Connect molecular evidence to uncertainty-aware models."],
        ["Validation", "Design analytical and translational evidence gates."],
      ],
      flowTitle: "Research pathway",
      flow: ["Discover", "Count", "Interpret", "Validate", "Report"],
      contextsTitle: "Connected portfolio",
      contexts: [
        ["LungDefend", "A governed product concept aligned to precision-oncology research workflows."],
        ["Bremony and ProsGlad", "Product concepts presented with explicit evidence and regional status."],
        ["DeteDx", "A detection-oriented concept connected to precision-assay evidence."],
      ],
      limitation: "No diagnostic, treatment, survival, or clinical benefit claim is made for these research concepts.",
      related: [["View product line", "/product-line", "Products and concepts mapped to Precision Oncology."]],
    }),
    "industrial-resilience": makeDetail({
      headline: "Design biological systems for observable industrial resilience.",
      introTitle: "Operational continuity depends on measurable interfaces.",
      intro:
        "Industrial Resilience connects process observation, biological materials, surface interfaces, verification, and evidence-led maintenance.",
      featureTitle: "Platform logic",
      features: [
        ["Detect", "Observe material, biological, or process signals relevant to continuity."],
        ["Characterize", "Describe the mechanism, condition, and decision boundary."],
        ["Intervene", "Evaluate controlled material or process concepts."],
        ["Verify", "Use defined readouts to determine whether the intervention meets its objective."],
      ],
      flowTitle: "Operational loop",
      flow: ["Detect", "Characterize", "Intervene", "Verify", "Maintain"],
      contextsTitle: "Connected portfolio",
      contexts: [
        ["BioWeave Buildup Film", "A materials-focused product and development program with distinct status pages."],
        ["OptiCull X", "An industrial research concept for evidence-led process observation."],
        ["Verification workflows", "Shared evidence gates connect process signals, intervention records, and operational review."],
      ],
      limitation: "Durability, compatibility, and operational performance require environment-specific validation.",
      related: [["Explore industrial products", "/product-line/bioweave-buildup-film", "Review the product-status page for BioWeave."]],
    }),
    "biodefense-cbrn-threat": makeDetail({
      headline: "Preparedness research with governed evidence and controlled access.",
      introTitle: "High-consequence contexts require strict boundaries.",
      intro:
        "This application area focuses on non-operational detection concepts, readiness workflows, evidence review, and responsible information access.",
      featureTitle: "Preparedness framework",
      features: [
        ["Detection concepts", "Public descriptions remain at a non-operational capability level."],
        ["Evidence governance", "Source, quality, review, and access classification travel together."],
        ["Readiness workflows", "Roles, escalation, and decision paths are made explicit."],
        ["Controlled access", "Sensitive technical detail is shared only through qualified channels."],
      ],
      flowTitle: "Governed workflow",
      flow: ["Frame", "Detect", "Review", "Escalate", "Document"],
      contextsTitle: "Connected portfolio",
      contexts: [
        ["Rynvetha", "A controlled research concept for preparedness and detection workflows."],
        ["Ulmyxen", "A program-linked concept subject to scientific and regulatory review."],
        ["Aquila Sentinel", "A controlled-access preparedness and sensing pipeline program."],
      ],
      status: "Governed public overview",
      access: "Public principles; sensitive evidence restricted to qualified review",
      limitation: "No operational threat procedures, detection performance, or deployment instructions are disclosed.",
      related: [["View Aquila Sentinel", "/pipeline/aquila-sentinel", "Review public program rationale and access boundaries."]],
    }),
    longevity: makeDetail({
      headline: "Study resilience and healthy-aging biology without overstating outcomes.",
      introTitle: "Longevity research is a systems question.",
      intro:
        "Medein studies cellular state, immune and microbiome evidence, biological tolerance, and measurable resilience within research-stage programs.",
      featureTitle: "Research framework",
      features: [
        ["Cellular state", "Measure state transitions and context rather than relying on a single marker."],
        ["Systems resilience", "Evaluate how interacting systems respond to defined stress or recovery conditions."],
        ["Microbiome evidence", "Use quality-aware microbiome signals within broader biological context."],
        ["Healthspan mechanisms", "Focus on measurable research mechanisms instead of lifespan promises."],
      ],
      flowTitle: "Evidence pathway",
      flow: ["Observe", "Model", "Perturb", "Measure", "Review"],
      contextsTitle: "Connected portfolio",
      contexts: [
        ["ColoLite+", "A healthy-aging research product concept with region-specific status."],
        ["Tolerivex", "A pipeline program focused on tolerance and resilience mechanisms."],
        ["Methuselon 969", "A program focused on cellular resilience and evidence development."],
      ],
      limitation: "No life-extension, rejuvenation, disease-prevention, or clinical-outcome claim is made.",
      related: [["Explore longevity pipeline", "/pipeline/tolerivex", "Review research rationale, stage, and partnering."]],
    }),
  },
  "product-line": {
    lungdefend: productDetail({
      headline: "A governed concept for precision-oncology research workflows.",
      introTitle: "A product concept must keep use, evidence, and status aligned.",
      intro:
        "LungDefend is presented as a precision-oncology product concept connecting defined research questions, molecular evidence, interpretation, and review.",
      features: [
        ["Research context", "The intended question and population context are declared."],
        ["Measurement plan", "Assay choice follows the signal and evidence requirement."],
        ["Interpretation boundary", "Outputs include uncertainty and limitations."],
        ["Review record", "Scientific and regional status are maintained separately."],
      ],
      flow: ["Qualify inquiry", "Define evidence need", "Review concept", "Assess fit", "Confirm next step"],
      contexts: [["Research collaboration", "Qualified precision-oncology research programs."], ["Assay evaluation", "Measurement and evidence-development discussions."], ["Scientific briefing", "Non-confidential program review with the Medein team."]],
      status: "Product concept",
      limitation: "Not presented as an approved diagnostic, prevention, or treatment product.",
      related: [["Precision Oncology", "/applications/precision-oncology", "See the application framework behind this concept."]],
    }),
    bremony: productDetail({
      headline: "A biomarker-oriented product concept with explicit evidence status.",
      introTitle: "Biomarker signals need context before interpretation.",
      intro:
        "Bremony organizes a precision-oncology product concept around biomarker context, assay quality, interpretation boundaries, and region-specific review.",
      features: [["Biomarker context", "The biological question and intended research decision are stated."], ["Assay quality", "Analytical dimensions are reviewed for the selected format."], ["Interpretation", "Evidence strength and uncertainty remain visible."], ["Status control", "Research, product, and regional status are not conflated."]],
      flow: ["Frame", "Measure", "Interpret", "Review", "Document"],
      contexts: [["Biomarker research", "Collaborative program evaluation."], ["Evidence development", "Analytical and translational planning."], ["Partner briefing", "Public and controlled material shared at the appropriate access level."]],
      status: "Product concept",
      limitation: "No clinical utility or patient-outcome claim is made.",
    }),
    prosglad: productDetail({
      headline: "A precision-oncology concept designed around controlled claims.",
      introTitle: "Precision requires traceable decisions.",
      intro:
        "ProsGlad is a product concept connecting target definition, measurement, quality review, and evidence-aware communication.",
      features: [["Target definition", "The research target and use boundary are explicit."], ["Controls", "Controls and quality rules are part of the proposed workflow."], ["Evidence review", "Outputs are evaluated against the defined decision need."], ["Communication", "Public language is limited to supported evidence."]],
      flow: ["Define", "Design", "Measure", "Review", "Communicate"],
      contexts: [["Research-use concept", "Qualified scientific collaboration."], ["Assay planning", "Analytical strategy and control review."], ["Portfolio evaluation", "Assessment within the broader precision-oncology system."]],
      status: "Product concept",
      limitation: "Not represented as an approved screening, diagnostic, or therapeutic product.",
    }),
    detedx: productDetail({
      headline: "A detection-oriented concept connected to governed assay evidence.",
      introTitle: "Detection is only useful when quality and limitations travel with the result.",
      intro:
        "DeteDx frames detection around direct signals, calibration, decision thresholds, and auditable reporting.",
      features: [["Signal definition", "The target signal and matrix are specified."], ["Calibration", "Raw events are connected to interpretable outputs."], ["Quality control", "Controls and exception rules are visible."], ["Reporting", "Limitations accompany the reported result."]],
      flow: ["Capture", "Classify", "Calibrate", "Review", "Report"],
      contexts: [["Assay research", "Detection workflow development."], ["Low-abundance signals", "Programs requiring careful signal interpretation."], ["Quality evaluation", "Comparative measurement and control studies."]],
      status: "Product concept",
      limitation: "No diagnostic sensitivity, specificity, or clinical performance is claimed.",
      related: [["Direct Molecular Counting", "/science/direct-molecular-counting", "Explore the supporting measurement concept."]],
    }),
    "cololite-plus": productDetail({
      headline: "A healthy-aging research concept grounded in systems evidence.",
      introTitle: "Microbiome signals belong in a wider biological context.",
      intro:
        "ColoLite+ is positioned as a longevity research product concept connecting microbiome evidence, cellular state, and quality-aware interpretation.",
      features: [["Systems context", "Microbiome signals are considered alongside broader biological evidence."], ["Measurement quality", "Sampling, controls, and interpretation limits are declared."], ["Longitudinal view", "Change over time is separated from causal or outcome claims."], ["Region status", "Availability and evidence are confirmed by qualified request."]],
      flow: ["Define context", "Collect", "Measure", "Interpret", "Review"],
      contexts: [["Microbiome research", "Quality-aware exploratory studies."], ["Healthy-aging biology", "Mechanism-focused research programs."], ["Partner evaluation", "Scientific-fit and evidence discussions."]],
      status: "Product concept",
      limitation: "No lifespan, rejuvenation, prevention, or treatment claim is made.",
      related: [["Longevity application", "/applications/longevity", "Review the broader research framework."]],
    }),
    rynvetha: productDetail({
      headline: "A controlled preparedness concept for governed detection research.",
      introTitle: "Preparedness concepts require strict information boundaries.",
      intro:
        "Rynvetha is a non-operational research concept focused on evidence routing, readiness review, and controlled access.",
      features: [["Governed scope", "Public material remains at capability level."], ["Evidence routing", "Sources and quality classifications are explicit."], ["Readiness review", "Decision roles and escalation paths are defined."], ["Access control", "Sensitive detail is restricted to qualified channels."]],
      flow: ["Qualify", "Frame", "Review", "Route", "Document"],
      contexts: [["Preparedness research", "Non-operational scientific collaboration."], ["Governance design", "Evidence and access workflow review."], ["Controlled briefing", "Qualified discussion under appropriate safeguards."]],
      status: "Controlled research concept",
      limitation: "No operational procedures, threat-enablement details, or validated detection performance are disclosed.",
    }),
    ulmyxen: productDetail({
      headline: "A program-linked concept for responsible sensing research.",
      introTitle: "Responsible sensing starts with governance.",
      intro:
        "Ulmyxen connects research sensing concepts to evidence classification, scientific review, and controlled communication.",
      features: [["Sensing context", "The research question and environment are bounded."], ["Evidence classification", "Quality and access status are attached to each record."], ["Review workflow", "Scientific and governance review precede external sharing."], ["Controlled communication", "Public content excludes sensitive operational detail."]],
      flow: ["Scope", "Sense", "Classify", "Review", "Escalate"],
      contexts: [["Sensing research", "Non-operational measurement concepts."], ["Evidence governance", "Review and routing-system design."], ["Qualified collaboration", "Controlled technical discussion after screening."]],
      status: "Controlled research concept",
      limitation: "No operational deployment or detection-performance claim is made.",
      related: [["Aquila Sentinel", "/pipeline/aquila-sentinel", "View the related controlled-access pipeline program."]],
    }),
    "bioweave-buildup-film": productDetail({
      headline: "Engineer protective buildup films for industrial continuity.",
      introTitle: "A product page distinct from the development program.",
      intro:
        "This page presents BioWeave as an Industrial Resilience product concept. Intended use, material compatibility, evidence, and regional availability remain separate from the pipeline development page.",
      features: [["Material architecture", "Layer and interface concepts are defined against the intended environment."], ["Surface compatibility", "Substrate and process constraints are part of evaluation."], ["Durability evidence", "Performance dimensions are selected for the actual use context."], ["Documentation", "Status, specification, and review records remain linked."]],
      flow: ["Define surface", "Select architecture", "Evaluate compatibility", "Validate evidence", "Confirm use"],
      contexts: [["Industrial surfaces", "Qualified material-interface evaluation."], ["Process continuity", "Research on resilient operating environments."], ["Partner validation", "Application-specific evidence programs."]],
      status: "Research product concept",
      limitation: "No universal durability, protection, or compatibility performance is claimed.",
      related: [["BioWeave pipeline", "/pipeline/bioweave-buildup-film", "Review the distinct development-program page."]],
    }),
    "opticull-x": productDetail({
      headline: "Observe industrial biological systems with decision-focused evidence.",
      introTitle: "Operational observation needs defined signals and thresholds.",
      intro:
        "OptiCull X is an Industrial Resilience research concept connecting observation, signal classification, verification, and maintenance decisions.",
      features: [["Observation design", "Signals are selected around the operational question."], ["Classification", "Thresholds and exception rules are explicit."], ["Verification", "Outputs are compared with independent evidence where appropriate."], ["Operational record", "Changes and decisions remain traceable."]],
      flow: ["Observe", "Classify", "Verify", "Decide", "Monitor"],
      contexts: [["Bioprocess observation", "Research into interpretable process signals."], ["Quality systems", "Evidence-led monitoring concepts."], ["Industrial research", "Partner programs with environment-specific validation."]],
      status: "Research concept",
      limitation: "No universal optimization, yield, or operational performance is claimed.",
    }),
  },
  pipeline: {
    tolerivex: makeDetail({
      headline: "A longevity research program focused on tolerance and resilience mechanisms.",
      introTitle: "Program rationale",
      intro:
        "Tolerivex explores how biological systems maintain function across defined stress and recovery contexts, with emphasis on measurable mechanisms and evidence gates.",
      featureTitle: "Development framework",
      features: [["Mechanism definition", "Specify the tolerance or resilience question."], ["Model selection", "Choose systems appropriate to the hypothesis."], ["Measurement plan", "Define state, response, and recovery readouts."], ["Evidence gate", "Advance only when the next decision threshold is met."]],
      flowTitle: "Current development path",
      flow: ["Discovery", "Model qualification", "Mechanism evidence", "Program validation", "Translation review"],
      contextsTitle: "Partnering opportunities",
      contexts: [["Research models", "Collaborative model and assay development."], ["Mechanism evidence", "Data and biomarker research."], ["Translation planning", "Evidence and program-design collaboration."]],
      status: "Research program—discovery and evidence development",
      limitation: "No healthspan, disease, clinical, or lifespan outcome is claimed.",
    }),
    "methuselon-969": makeDetail({
      headline: "A cellular-resilience program built around measurable evidence.",
      introTitle: "Program rationale",
      intro:
        "Methuselon 969 organizes cellular-state and resilience research around defined perturbations, longitudinal measurement, and explicit review gates.",
      featureTitle: "Program components",
      features: [["State model", "Define the cellular state and relevant transitions."], ["Perturbation design", "Use controlled research conditions to test hypotheses."], ["Longitudinal measurement", "Track change, recovery, and uncertainty over time."], ["Review gate", "Separate exploratory signals from validated program evidence."]],
      flowTitle: "Development path",
      flow: ["Hypothesis", "Feasibility", "Longitudinal evidence", "Independent review", "Translation decision"],
      contextsTitle: "Partnering opportunities",
      contexts: [["Cell-state models", "Collaborative model development."], ["Precision assays", "Program-specific measurement and validation."], ["Data interpretation", "Uncertainty-aware longitudinal analysis."]],
      status: "Research program—feasibility planning",
      limitation: "No rejuvenation, anti-aging, prevention, or clinical-performance claim is made.",
    }),
    "bioweave-buildup-film": makeDetail({
      headline: "A development program for adaptive, durable buildup-film systems.",
      introTitle: "Program rationale",
      intro:
        "The BioWeave pipeline program develops material architectures, surface compatibility evidence, and application-specific validation distinct from the public product concept.",
      featureTitle: "Development components",
      features: [["Material architecture", "Evaluate layer and interface concepts."], ["Compatibility", "Study substrate, environment, and process constraints."], ["Durability evidence", "Define meaningful mechanical and operational dimensions."], ["Scale-up review", "Assess repeatability, documentation, and partner requirements."]],
      flowTitle: "Development path",
      flow: ["Material discovery", "Interface feasibility", "Application validation", "Scale-up review", "Product decision"],
      contextsTitle: "Partnering opportunities",
      contexts: [["Material research", "Architecture and characterization programs."], ["Application validation", "Environment-specific evidence development."], ["Manufacturing review", "Process and documentation readiness."]],
      status: "Research program—materials and application validation",
      limitation: "The pipeline status does not imply current commercial availability or universal performance.",
      related: [["BioWeave product page", "/product-line/bioweave-buildup-film", "Review the separate product-status page."]],
    }),
    "aquila-sentinel": makeDetail({
      headline: "A controlled-access preparedness and sensing program.",
      introTitle: "Program rationale",
      intro:
        "Aquila Sentinel explores governed sensing, evidence classification, and readiness workflows for high-consequence biological and CBRN contexts.",
      featureTitle: "Public program components",
      features: [["Governed sensing", "Public material remains non-operational."], ["Evidence classification", "Quality, sensitivity, and access are reviewed separately."], ["Readiness workflow", "Roles, escalation, and documentation are defined."], ["Controlled briefing", "Qualified partners receive only appropriately reviewed information."]],
      flowTitle: "Program path",
      flow: ["Scope", "Govern", "Evaluate", "Review", "Control access"],
      contextsTitle: "Partnering opportunities",
      contexts: [["Governance systems", "Evidence, access, and escalation design."], ["Non-operational sensing research", "High-level measurement collaboration."], ["Preparedness review", "Qualified public-sector or institutional discussions."]],
      status: "Controlled research program",
      access: "Public principles; deeper material requires qualification and controlled briefing",
      limitation: "No operational protocols, threat-enablement information, or validated deployment performance are disclosed.",
    }),
  },
};

export const insightArticles = [
  {
    slug: "designing-with-biological-complexity",
    category: "Design",
    title: "Designing with biological complexity",
    summary: "How modular, context-aware systems help teams make biological design decisions without hiding uncertainty.",
    image: assetUrl("/assets/optimized/insights-team.webp"),
    takeaways: ["Start from a decision, not a model.", "Keep assumptions attached to outputs.", "Design validation as part of the system."],
    sections: [
      ["Complexity is the material", "Biological systems vary across context, time, scale, and measurement. A useful design architecture does not erase that variation; it makes it reviewable."],
      ["Modularity creates review points", "Separating context, design, simulation, assay, and evidence allows specialists to inspect the interfaces where assumptions enter."],
      ["Evidence closes the loop", "Validation results should update the design record, confidence, and next experiment—not sit in a disconnected report."],
    ],
  },
  {
    slug: "evidence-frameworks-for-programmable-life",
    category: "Evidence",
    title: "Evidence frameworks for programmable life",
    summary: "A practical framework for making evidence level, review date, limitations, and access status visible.",
    image: assetUrl("/assets/optimized/insight-dna.webp"),
    takeaways: ["Separate content status from program status.", "Publish limitations beside claims.", "Treat access classification as evidence metadata."],
    sections: [
      ["Evidence has more than one dimension", "Strength, relevance, review status, version, region, and access classification all affect what a statement can support."],
      ["Public content needs a boundary", "A clear public overview can explain purpose and architecture while keeping sensitive procedures and unreviewed performance outside the page."],
      ["Review is continuous", "Content owners and scientific reviewers should update status when the program, evidence, or intended audience changes."],
    ],
  },
  {
    slug: "from-platform-architecture-to-product-systems",
    category: "Systems",
    title: "From platform architecture to product systems",
    summary: "How shared interfaces connect science, translation, products, and pipeline programs.",
    image: assetUrl("/assets/optimized/lab-branded.webp"),
    takeaways: ["Platforms create reusable interfaces.", "Products need explicit status.", "Pipeline pages should communicate gates, not promises."],
    sections: [
      ["Reuse comes from interfaces", "A platform becomes reusable when evidence, workflows, and review responsibilities can move between programs without losing context."],
      ["Products and pipeline are different records", "A product page communicates intended use and availability. A pipeline page communicates rationale, stage, evidence gate, and partnering."],
      ["Governance supports scale", "Shared review fields make it easier to expand the portfolio without turning early research into unsupported claims."],
    ],
  },
];
